import * as dateFns from 'date-fns';
import type { HistoricVesselPoint } from '../maritime-menu-options/history-panel/historic-vessel-point.model';
import type { LocationMessage } from '../models/vessels/internal-ais-api-v3.model';
import type { HistoricVesselData } from '../models/vessels/maritime-ais-api';
import { Vessel, VesselSource } from '../models/vessels/vessel.model';
import store from '../store';
import {
  DefaultHistoryDuration,
  DefaultHistoryFrequency,
} from '../user-settings/user-preferences/user-preferences.slice';
import retryAsync from '../utils/retry';
import { wrapRequest } from './base';
import { getLocationMessages } from './internal-ais-api-v3';

/**
 * Convert a flat array of v2 `LocationMessage` records to the internal
 * `HistoricVesselPoint` shape.  Records without a position (no `latitude` or
 * `longitude`) are silently dropped.
 */
const mapLocationMessagesToHistoricVesselPoints = (
  messages: LocationMessage[]
): HistoricVesselPoint[] =>
  messages.reduce<HistoricVesselPoint[]>((acc, msg) => {
    if (msg.latitude === undefined || msg.longitude === undefined) {
      return acc;
    }

    acc.push({
      id: `${msg.vesselUid}-${msg.posDt}`,
      vessel_id: String(msg.vesselUid),
      name: msg.vesselName ?? '',
      imo: msg.imo,
      mmsi: msg.mmsi,
      timestamp: msg.posDt * 1000,
      created_at: msg.insertDt !== undefined ? msg.insertDt * 1000 : undefined,
      latitude: msg.latitude,
      longitude: msg.longitude,
      heading: msg.heading,
      speed: msg.sog ?? 0,
      course: msg.cog ?? 0,
      rot: msg.rot ?? 0,
      status: msg.navStatus,
      callsign: msg.callsign ?? '',
      shiptype: msg.vesselType,
      flag: msg.flag,
      collection_type: msg.posSrc ?? '',
      maneuver: 0,
      source: VesselSource.AIS,
      destination: msg.destination,
      eta: msg.eta,
      draught: msg.draught,
    });

    return acc;
  }, []);

const getSampleInterval = (
  default_history: { frequency: DefaultHistoryFrequency } | undefined
): string => {
  if (default_history?.frequency === DefaultHistoryFrequency.HOURLY) {
    return '1h';
  }
  if (default_history?.frequency === DefaultHistoryFrequency.DAILY) {
    return '1d';
  }
  return '10m';
};

/**
 * Add one sample-interval worth of time to a Date so that the API's upper
 * bound (which appears to be exclusive) returns the point that sits exactly
 * at `endDate`.  After fetching we filter any messages with posDt > endDate
 * back out, so callers always see at most `endDate` as the latest point.
 */
const addOneSampleInterval = (date: Date, sampleInterval: string): Date => {
  if (sampleInterval === '1d') return dateFns.addDays(date, 1);
  if (sampleInterval === '1h') return dateFns.addHours(date, 1);
  // default: '10m'
  return dateFns.addMinutes(date, 10);
};

// Max identifiers (MMSIs or IMOs) per parallel API request.
const MMSI_BATCH_SIZE = 500;

/**
 * Fetch the single most-recent AIS position for each of the given MMSIs,
 * returning a Map keyed by MMSI string for O(1) lookup.
 *
 * MMSIs are split into batches of up to 500 and all batches are requested in
 * parallel; the results are merged into a single Map.
 */
export const getMostRecentVesselLocationsByMmsi = async (
  mmsis: number[],
  callerFeature: string
): Promise<Map<string, LocationMessage>> => {
  const batches: number[][] = [];
  for (let i = 0; i < mmsis.length; i += MMSI_BATCH_SIZE) {
    batches.push(mmsis.slice(i, i + MMSI_BATCH_SIZE));
  }

  const responses = await Promise.all(
    batches.map((batch) =>
      getLocationMessages({
        callerFeature,
        body: {
          mmsis: batch,
          most_recent: true,
        },
      })
    )
  );

  const map = new Map<string, LocationMessage>();
  for (const response of responses) {
    const { data } = response;
    if (Array.isArray(data)) {
      for (const msg of data) {
        map.set(String(msg.mmsi), msg);
      }
    }
  }
  return map;
};

/**
 * Fetch AIS position history for a single vessel up to a given end date,
 * using the location_messages_v2 API.
 *
 * The window length and sample interval are driven by the user's history preferences:
 * - end   = endDate (caller decides — e.g. vessel's last known position, or event time + offset)
 * - start = end minus 7 days (or 30 days when user prefs are set to MONTH)
 */
export const getVesselTrackPointsV3 = async (
  mmsi: string,
  endDate: Date,
  callerFeature = 'vessel-track'
): Promise<HistoricVesselPoint[]> => {
  const { default_history } = store.getState().userPreferences.userPreferences;

  const days =
    default_history?.duration === DefaultHistoryDuration.MONTH ? 30 : 7;
  const sampleInterval = getSampleInterval(default_history);
  const startDate = dateFns.subDays(endDate, days);

  const startDatetime = startDate.toISOString();
  // Request slightly beyond endDate so the API (exclusive upper bound) returns
  // the point that sits exactly at endDate.
  const bufferedEndDate = addOneSampleInterval(endDate, sampleInterval);
  const endDatetime = bufferedEndDate.toISOString();

  const allMessages: LocationMessage[] = [];
  let cursor: string | undefined;

  do {
    // biome-ignore lint/performance/noAwaitInLoops: each request's cursor depends on the previous response, so calls must be sequential
    const response = await getLocationMessages({
      callerFeature,
      body: {
        mmsis: [parseInt(mmsi, 10)],
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        sample_interval: sampleInterval,
        ...(cursor ? { cursor } : {}),
      },
    });

    const { data } = response;
    if (Array.isArray(data)) {
      allMessages.push(...data);
    }
    cursor = response.cursor;
  } while (cursor);

  // Filter out any messages that fall in the buffer zone (after endDate).
  const endMs = endDate.getTime();
  const filteredMessages = allMessages.filter(
    (msg) => msg.posDt * 1000 <= endMs
  );

  const points = mapLocationMessagesToHistoricVesselPoints(filteredMessages);
  return points.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Convert a `LocationMessage` from the v3 AIS API to a `Vessel`.
 * Returns `null` when the message has no position (latitude/longitude absent).
 */
const locationMessageToVessel = (
  msg: LocationMessage | null | undefined
): Vessel | null => {
  if (!msg || msg.latitude === undefined || msg.longitude === undefined) {
    return null;
  }

  return {
    vessel_id: String(msg.vesselUid),
    name: msg.vesselName ?? '',
    imo: msg.imo,
    mmsi: msg.mmsi,
    latitude: msg.latitude,
    longitude: msg.longitude,
    heading: msg.heading,
    speed: msg.sog,
    course: msg.cog ?? 0,
    callsign: msg.callsign ?? '',
    shiptype: msg.vesselType,
    flag: msg.flag,
    timestamp: msg.posDt * 1000,
    source: VesselSource.AIS,
    destination: msg.destination,
    eta: msg.eta,
    draught: msg.draught,
  };
};

/**
 * Initialise a v3 AIS WebSocket query filtered by vessel identifiers
 * (MMSIs and/or IMOs) rather than a geographic area.
 *
 * Calls the same backend proxy endpoint as the geometry variant but sends
 * `mmsis`/`imos` in place of `geometry`.  The token must be obtained
 * beforehand via `getV3Token` — each token is single-use.
 *
 * @param mmsis - Array of MMSI numbers to query (may be empty if imos provided).
 * @param imos  - Array of IMO numbers to query (may be empty if mmsis provided).
 * @param token - One-time JWT from `getV3Token`.
 * @param startDate - Start of the time window (inclusive).
 * @param endDate   - End of the time window (exclusive).
 * @param sampleInterval - Sampling window per vessel, e.g. "10m", "1h", "1d".
 * @param callerFeature  - Name of the calling feature for logging/monitoring.
 */
export const initialiseByV3LocationWebsocketByIdentifiers = async ({
  mmsis,
  imos,
  token,
  startDate,
  endDate,
  sampleInterval,
  callerFeature,
}: {
  mmsis: number[];
  imos: number[];
  token: string;
  startDate: Date;
  endDate: Date;
  sampleInterval: string;
  callerFeature: string;
}): Promise<void> => {
  const response = await retryAsync(
    () =>
      wrapRequest('post', 'geonius', `/v2/ais-data`, {
        headers: {
          'x-caller-product': 'Geollect',
          'x-caller-feature': callerFeature,
        },
        body: {
          ...(mmsis.length > 0 ? { mmsis } : {}),
          ...(imos.length > 0 ? { imos } : {}),
          start_datetime: startDate.toISOString(),
          end_datetime: endDate.toISOString(),
          sample_interval: sampleInterval,
          token,
        },
        queryStringParameters: {
          upstream_path: 'websocket/location_messages_v2',
        },
      }),
    3,
    1000
  );

  if (response.errors) {
    throw new Error(
      `Error initialising identifier websocket: ${response.errors.join(', ')}`
    );
  }
};

/**
 * Group a flat array of v3 `LocationMessage` records by vessel and return the
 * `HistoricVesselData[]` shape consumed by `prependVesselHistoryData`.
 *
 * Messages without a position are included in the point list only if they
 * survive `mapLocationMessagesToHistoricVesselPoints` (which silently drops
 * them).  Vessel groups where no positioned message exists are omitted.
 */
export const groupLocationMessagesToVesselHistoryData = (
  messages: LocationMessage[]
): HistoricVesselData[] => {
  const groups = new Map<number, LocationMessage[]>();

  for (const msg of messages) {
    if (!groups.has(msg.vesselUid)) {
      groups.set(msg.vesselUid, []);
    }
    groups.get(msg.vesselUid)!.push(msg);
  }

  const result: HistoricVesselData[] = [];

  for (const vesselMessages of groups.values()) {
    const latestPositioned = vesselMessages.reduce<LocationMessage | null>(
      (best, msg) => {
        if (msg.latitude === undefined || msg.longitude === undefined)
          return best;
        if (!best) return msg;
        return msg.posDt > best.posDt ? msg : best;
      },
      null
    );
    const vessel = locationMessageToVessel(latestPositioned);
    // biome-ignore lint/style/noContinue: early loop exit is clearer than nesting remaining logic in an if block
    if (!vessel) continue;

    result.push({
      vessel,
      messages: mapLocationMessagesToHistoricVesselPoints(vesselMessages),
    });
  }

  return result;
};
